// Puente entre nuestros botones propios (quick add de las tarjetas y "comprados
// juntamente") y el carrito de Horizon.
//
// Por que existe: antes aniadiamos con fetch y luego reescribiamos a mano el innerHTML de
// <cart-drawer-component>. Eso destruye y recrea <cart-items-component>, que en ese mismo
// momento esta procesando su propio refresco, y segun quien gane la carrera el cajon se
// queda SIN las lineas del carrito: se ven la barra de envio, el upsell y el total, pero no
// los productos. Reportado por el cliente el 15-sep.
//
// Lo correcto es hacer lo mismo que hace el formulario de producto del tema: pedir las
// secciones del carrito en la propia llamada a /cart/add.js y disparar CartLinesUpdateEvent
// con esas secciones. A partir de ahi el tema se refresca solo, abre el cajon si toca y
// nosotros no tocamos el DOM del carrito.
import { CartLinesUpdateEvent } from '@shopify/events';

/** Ids de seccion de todos los <cart-items-component> vivos (cajon y pagina de carrito). */
function seccionesDelCarrito() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  });
  return ids;
}

function rutaAnadir() {
  return (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
}

/**
 * Anade lineas al carrito y avisa al tema.
 * @param {Array<{id: number|string, quantity: number, selling_plan?: number|string}>} lineas
 * @param {string} [origen] - Para poder distinguir quien lo pidio en los listeners.
 * @returns {Promise<object>} el carrito ya actualizado
 */
async function anadir(lineas, origen) {
  const secciones = seccionesDelCarrito();
  const diferido = CartLinesUpdateEvent.createPromise();
  const unidades = lineas.reduce((n, l) => n + (Number(l.quantity) || 0), 0);

  // Se dispara desde el body a proposito: si saliera de dentro de un <dialog> modal, el
  // cajon aplaza su apertura hasta que ese dialogo se cierre.
  document.body.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lineas.map((l) => ({ merchandiseId: String(l.id), quantity: Number(l.quantity) || 1 })),
      promise: diferido.promise,
    })
  );

  try {
    const cuerpo = { items: lineas };
    if (secciones.length) cuerpo.sections = secciones.join(',');

    const respuesta = await fetch(rutaAnadir(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(cuerpo),
    }).then((r) => r.json());

    if (respuesta.status) {
      throw new Error(respuesta.description || respuesta.message || 'Add to cart failed');
    }

    const carrito = await fetch('/cart.js').then((r) => r.json());

    diferido.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(carrito),
      detail: {
        items: carrito.items,
        source: origen || 'sc-card',
        itemCount: unidades,
        sections: respuesta.sections,
        didError: false,
      },
    });

    // El refresco de las lineas va en el .then del propio evento, asi que se espera un
    // fotograma antes de abrir para no ensenar el cajon a medio pintar.
    requestAnimationFrame(() => requestAnimationFrame(abrirCajon));

    return carrito;
  } catch (error) {
    diferido.reject(error);
    throw error;
  }
}

/**
 * Abre el cajon del carrito usando el <theme-drawer> del propio tema.
 *
 * Hace falta porque <cart-drawer-component> de esta tienda NO lleva el atributo
 * auto-open: el tema solo abre el cajon cuando lo pide el formulario de producto. Se
 * abre DESPUES de que el evento haya refrescado las lineas, no antes.
 */
function abrirCajon() {
  const cajon = document.querySelector('cart-drawer-component');
  const drawer = cajon && cajon.closest('theme-drawer');
  if (drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('open')) {
    drawer.open();
    return true;
  }
  return false;
}

window.scCarrito = { anadir, seccionesDelCarrito, abrirCajon };
document.dispatchEvent(new CustomEvent('sc:carrito:listo'));
